import { action, makeObservable, runInAction } from 'mobx';

import type { moment } from '@feathr/hooks';
import { shortCodeToken, toQueryString } from '@feathr/hooks';
import type {
  Attributes,
  ICollectionStore,
  IMetadata,
  IRachisMessage,
  IWretchResponseValid,
} from '@feathr/rachis';
import type { IListParams, ListResponse } from '@feathr/rachis';
import { BulkCollection, isWretchError, wretch } from '@feathr/rachis';

import type {
  Conversion,
  Conversions,
  ICategoriesResponse,
  IConversion,
  IConversionFilters,
} from '../conversions';
import type { TAttributionModel } from '../stats_aggregated';
import type { ITemplate } from '../templates';
import { TemplateClass } from '../templates';
import { AffinityCampaign } from './affinity';
import { AutoPinpointEmailCampaign } from './auto_pinpoint_email';
import type { Campaign } from './campaign';
import { BaseCampaign } from './campaign';
import { ConversationCampaign } from './conversation';
import { DripCampaign } from './drip';
import { DripStepCampaign } from './drip_step';
import { EmailListCampaign } from './email_list';
import { EmailListFacebookCampaign } from './email_list_facebook';
import { FacebookCampaign } from './facebook';
import { GoogleAdsSearchCampaign } from './google_ads_search';
import { GoogleAdsSmartCampaign } from './google_ads_smart';
import type { TLandingPageCampaign } from './landing_page';
import { LandingPageCampaign } from './landing_page';
import { LookalikeCampaign } from './lookalike';
import { MailrailAutoSendEmailCampaign } from './mailrail_auto_send';
import { MailrailDripEmailCampaign } from './mailrail_drip';
import { MailrailPartnerMessage } from './mailrail_partner_message';
import { MailrailSingleSendEmailCampaign } from './mailrail_single_send';
import { MobileGeoFenceRetargetingCampaign } from './mobile_geo_fence_retargeting';
import { MobileGeoFencingCampaign } from './mobile_geo_fencing';
import { OmniCampaign } from './omni';
import { PinpointEmailCampaign } from './pinpoint_email';
import { PinpointPartnerMessage } from './pinpoint_partner_message';
import { ReferralCampaign } from './referral';
import { SearchCampaign } from './search';
import { SeedSegmentCampaign } from './seed_segment';
import { SegmentCampaign } from './segment';
import { SmartPinpointEmailCampaign } from './smart_pinpoint_email';
import { TrackedLinkCampaign } from './tracked_link';
import type { ECampaignObjective, IChannelBudgets } from './ttd_base';
import type {
  ICampaignAttributes,
  TAdChannelKey,
  TCampaignAttributes,
  TCampaignGroup,
} from './types';
import { CampaignClass } from './types';

import defaultPageTemplate from './defaultPageTemplate.json';

const {
  Affinity,
  AutoPinpointEmail,
  Conversation,
  Drip,
  DripStep,
  EmailList,
  EmailListFacebook,
  Facebook,
  GoogleAdsSearch,
  GoogleAdsSmart,
  LandingPage,
  Lookalike,
  MailrailAutoSend,
  MailrailDrip,
  MailrailPartnerMessage: MPartnerMessage,
  MailrailSingleSend,
  MobileGeoFenceRetargeting,
  MobileGeoFencing,
  Omni,
  PinpointEmail,
  PinpointPartnerMessage: PartnerMessage,
  Referral,
  Search,
  SeedSegment,
  Segment,
  SmartPinpointEmail,
  TrackedLink,
} = CampaignClass;

export const CampaignGroupMap: Record<TCampaignGroup, CampaignClass[]> = {
  ads: [
    Affinity,
    EmailList,
    EmailListFacebook,
    Facebook,
    GoogleAdsSearch,
    GoogleAdsSmart,
    Lookalike,
    MobileGeoFenceRetargeting,
    MobileGeoFencing,
    Search,
    SeedSegment,
    Segment,
    Omni,
  ],
  all: [
    Affinity,
    AutoPinpointEmail,
    Conversation,
    Drip,
    EmailList,
    EmailListFacebook,
    Facebook,
    GoogleAdsSearch,
    GoogleAdsSmart,
    LandingPage,
    Lookalike,
    MailrailAutoSend,
    MailrailDrip,
    MailrailSingleSend,
    MobileGeoFenceRetargeting,
    MobileGeoFencing,
    Omni,
    PinpointEmail,
    Referral,
    Search,
    SeedSegment,
    Segment,
    SmartPinpointEmail,
    TrackedLink,
  ],
  email: [
    AutoPinpointEmail,
    MailrailAutoSend,
    MailrailDrip,
    MailrailSingleSend,
    PinpointEmail,
    SmartPinpointEmail,
    Drip,
  ],
  'google-ads': [GoogleAdsSearch, GoogleAdsSmart],
  monetization: [
    Affinity,
    EmailList,
    Lookalike,
    MobileGeoFenceRetargeting,
    MobileGeoFencing,
    Omni,
    Search,
    Segment,
    SeedSegment,
  ],
  other: [Referral, Conversation, TrackedLink, LandingPage],
};

/**
 * Constructor plus the class-level API every campaign class carries. The
 * construct signature takes `any` because each concrete constructor accepts
 * only its own attribute shape — the correlation between `_cls` and the
 * constructor it selects is not expressible, so no shared parameter type
 * satisfies all of them.
 */
export interface ICampaignConstructor {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  new (attributes?: any): Campaign;
  readonly minDaysAdvance: number;
  getMinStartDate(): moment.Moment;
}

const campaignConstructorMap: Record<CampaignClass, ICampaignConstructor> = {
  [Affinity]: AffinityCampaign,
  [AutoPinpointEmail]: AutoPinpointEmailCampaign,
  [Conversation]: ConversationCampaign,
  [Drip]: DripCampaign,
  [DripStep]: DripStepCampaign,
  [EmailList]: EmailListCampaign,
  [EmailListFacebook]: EmailListFacebookCampaign,
  [Facebook]: FacebookCampaign,
  [GoogleAdsSearch]: GoogleAdsSearchCampaign,
  [GoogleAdsSmart]: GoogleAdsSmartCampaign,
  [LandingPage]: LandingPageCampaign,
  [Lookalike]: LookalikeCampaign,
  [MailrailAutoSend]: MailrailAutoSendEmailCampaign,
  [MailrailDrip]: MailrailDripEmailCampaign,
  [MPartnerMessage]: MailrailPartnerMessage,
  [MailrailSingleSend]: MailrailSingleSendEmailCampaign,
  [MobileGeoFencing]: MobileGeoFencingCampaign,
  [MobileGeoFenceRetargeting]: MobileGeoFenceRetargetingCampaign,
  [Omni]: OmniCampaign,
  [PartnerMessage]: PinpointPartnerMessage,
  [PinpointEmail]: PinpointEmailCampaign,
  [Referral]: ReferralCampaign,
  [Search]: SearchCampaign,
  [SeedSegment]: SeedSegmentCampaign,
  [Segment]: SegmentCampaign,
  [SmartPinpointEmail]: SmartPinpointEmailCampaign,
  [TrackedLink]: TrackedLinkCampaign,
};

/**
 * Resolve a CampaignClass to its model class, falling back to BaseCampaign for
 * missing or unknown values. Gives no-instance contexts (e.g. the create
 * wizard before a model exists) access to class-level API like minDaysAdvance.
 */
export function getCampaignClass(cls?: CampaignClass): ICampaignConstructor {
  return (cls && campaignConstructorMap[cls]) || BaseCampaign;
}

export function disambiguateCampaign(attributes: Partial<TCampaignAttributes>): Campaign {
  return new (getCampaignClass(attributes._cls))(attributes);
}

export interface ICampaignPartnerStats extends Record<string, unknown> {
  error: string;
  p_id: string;
  views: number;
  clicks: number;
  conversions: number;
  leads: number;
}

export interface ICampaignPartnerStatsMetadata extends IMetadata {
  is_partial: boolean;
}

type TCollectionUrlVariant =
  | 'export'
  | 'export-all'
  | 'google.suggest.budget'
  | 'google.suggest.keyword'
  | 'google.suggest.ad'
  | 'google.location';

export interface IExportCampaignsProps {
  attribution_model?: TAttributionModel;
  columns: string[];
  email: string;
  filename?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  filters?: Record<string, any>;
  sort?: string[];
}

/** `IChannelBudgets` as it comes off the wire; indexable to satisfy `wretch`'s constraint. */
interface IMinimumBudgetResponse extends IChannelBudgets {
  [key: string]: unknown;
}

export interface IMinimumBudgetPayload {
  /** The channels the campaign will run on. */
  channels: TAdChannelKey[];
  /** The campaign's flight length in days. */
  duration: number;
  /** Meta charges per ad set, and a lookalike audience adds a second one. */
  has_lookalike: boolean;
  objective: ECampaignObjective;
}

export class Campaigns<
  Model extends Campaign<ICampaignAttributes> = Campaign<ICampaignAttributes>,
> extends BulkCollection<Model> {
  constructor(initialModels: Array<Partial<Attributes<Model>>>, store: ICollectionStore = {}) {
    super(initialModels, store);

    makeObservable(this);
  }

  public getModel(attributes: Partial<Attributes<Model>>): Model {
    return disambiguateCampaign(attributes) as Model;
  }

  public override url(variant: Exclude<TCollectionUrlVariant, 'export-all'>, value: string): string;
  public override url(variant: 'export-all'): string;
  public override url(): string;
  public override url(variant?: TCollectionUrlVariant, value?: string): string {
    const root = super.url(variant, value);
    const googlePath = `${this.getHostname()}integrations/google-ads`;
    const suggestPath = `${googlePath}/campaign/${value}/suggest`;

    switch (variant) {
      case 'export-all':
        return `${this.getHostname()}exports/campaigns`;

      case 'export':
        return `${root}${value}/export`;

      case 'google.suggest.budget':
        return `${suggestPath}/budget`;

      case 'google.suggest.keyword':
        return `${suggestPath}/keyword_theme`;

      case 'google.suggest.ad':
        return `${suggestPath}/ad`;

      case 'google.location':
        return `${googlePath}/location?term=${value}`;

      default:
        return `${this.getHostname()}flight_campaigns/`;
    }
  }

  public getClassName(): string {
    return 'campaigns';
  }

  public listConversions(
    id: string,
    params: IListParams<IConversion> = {},
    conversionsCollection: Conversions,
  ): ListResponse<Conversion> {
    const url = `campaigns/${id}/conversions`;
    return conversionsCollection.list(params, { url });
  }

  public async getConversionCategories(id: string, params: IConversionFilters): Promise<string[]> {
    const headers = this.getHeaders();
    const query = toQueryString(this.parseJSONParams({ filters: params }));
    const url = `${this.getHostname()}campaigns/${id}/conversions/categories?${query}`;
    const response = await wretch<ICategoriesResponse>(url, {
      method: 'GET',
      headers,
    });

    if (isWretchError(response)) {
      throw response.error;
    }
    return response.data.categories ?? [];
  }

  /**
   * Calculates the minimum budget for an Omni campaign that does not exist yet.
   *
   * Unlike `getRecommendedBudget`, this takes plain values instead of a saved campaign, so it can
   * quote a floor before anything is built in the platform.
   */
  public async calculateMinimumBudget(payload: IMinimumBudgetPayload): Promise<IChannelBudgets> {
    const response = await wretch<IMinimumBudgetResponse>(`${this.url()}budget_calculator`, {
      method: 'POST',
      body: JSON.stringify(payload),
      headers: this.getHeaders(),
    });

    if (isWretchError(response)) {
      throw response.error;
    }
    return response.data;
  }

  @action
  public async publish(model: Model): Promise<Model> {
    model.isUpdating = true;
    const response = await wretch<ICampaignAttributes>(`${this.url()}${model.id}/publish`, {
      method: 'POST',
      headers: this.getHeaders(),
    });
    if (isWretchError(response)) {
      runInAction(() => {
        model.isUpdating = false;
        model.isErrored = true;
        model.error = response.error;
      });
      return model;
    }
    return this.processJSONResponse(response);
  }

  public async getPartnerStats(
    id: string,
    start: string,
    end: string,
    attributionModel: TAttributionModel,
    sortParams: Array<{
      id: string;
      desc?: boolean;
    }>,
    paginationParams: { page: number; page_size: number },
  ): Promise<IWretchResponseValid<ICampaignPartnerStats[], ICampaignPartnerStatsMetadata>> {
    const params = {
      start,
      end,
      attribution_model: attributionModel,
      pagination: paginationParams,
      ordering: sortParams.map((s) => (s.desc ? `-${s.id}` : s.id)),
    };
    const search = toQueryString(this.parseJSONParams(params));
    const response = await wretch<ICampaignPartnerStats[], ICampaignPartnerStatsMetadata>(
      `${this.getHostname()}campaigns/${id}/partner_stats?${search}`,
      {
        method: 'GET',
        headers: this.getHeaders(),
      },
    );
    if (isWretchError(response)) {
      throw response.error;
    }
    return response;
  }

  public async exportPartnerStats(
    id: string,
    start: string,
    end: string,
    attributionModel: TAttributionModel,
    sortParams: Array<{
      id: string;
      desc?: boolean;
    }>,
    paginationParams: { page: number; page_size: number },
    email: string,
  ): Promise<IRachisMessage> {
    const params = {
      partner_stats: '1',
      start,
      end,
      email,
      attribution_model: attributionModel,
      pagination: paginationParams,
      ordering: sortParams.map((s) => (s.desc ? `-${s.id}` : s.id)),
    };
    const search = toQueryString(this.parseJSONParams(params));
    const response = await wretch<IRachisMessage>(
      `${this.getHostname()}campaigns/${id}/export?${search}`,
      {
        method: 'GET',
        headers: this.getHeaders(),
      },
    );

    if (isWretchError(response)) {
      throw response.error;
    }
    return response.data;
  }

  public async exportCampaigns({
    // eslint-disable-next-line @typescript-eslint/naming-convention
    attribution_model,
    columns,
    email,
    filename,
    filters,
    sort,
  }: IExportCampaignsProps): Promise<IRachisMessage> {
    const response = await wretch<IRachisMessage>(this.url('export-all'), {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({ attribution_model, columns, email, filename, filters, sort }),
    });

    if (isWretchError(response)) {
      throw response.error;
    }
    return response.data;
  }

  public async addCampaign(
    type: CampaignClass,
    eventId: string,
    overrides: Partial<ICampaignAttributes> = {},
  ): Promise<Model> {
    const campaignAttrs: Partial<ICampaignAttributes> = {
      _cls: type,
      event: eventId,
      parent: eventId,
    };

    if (type === LandingPage) {
      (campaignAttrs as TLandingPageCampaign).template = {
        ...defaultPageTemplate,
        _cls: TemplateClass.Page,
        short_code: shortCodeToken(12),
      } as unknown as ITemplate;
    } else if (type === TrackedLink) {
      campaignAttrs.domain_id = '000000000000000000000000';
    }
    const campaign = this.create({ ...campaignAttrs, ...overrides } as Partial<Attributes<Model>>);
    return this.add(campaign, { validate: false });
  }

  // Clone a campaign using the custom clone endpoint for campaigns.
  // This will create a new campaign in the backend and send an email to the user when the process is complete.
  public async cloneCampaign(
    id: string,
    data?: Partial<Attributes<Model>> | undefined,
  ): Promise<void> {
    const body = data ? this.patchToString(data) : undefined;
    const response = await wretch<ICampaignAttributes>(`${this.url()}${id}/clone`, {
      method: 'POST',
      headers: this.getHeaders(),
      body,
    });

    if (isWretchError(response)) {
      throw response.error;
    }
    return;
  }

  /**
   * Move a campaign to a different project.
   * @param id - The ID of the campaign to move.
   * @param destinationProjectId - The ID of the project to move the campaign to.
   */
  @action
  public async moveCampaign(id: string, destinationProjectId: string): Promise<Model> {
    const model = this.modelsById.get(id);
    if (model) {
      model.isUpdating = true;
    }

    const response = await wretch<ICampaignAttributes>(`${this.url()}${id}/move`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({ destination_project: destinationProjectId }),
    });

    if (isWretchError(response)) {
      if (model) {
        runInAction(() => {
          model.isUpdating = false;
          model.isErrored = true;
          model.error = response.error;
        });
      }
      throw response.error;
    }

    // Refresh the cache so the campaign appears in the destination project's list
    this.refreshApiCache();

    return this.processJSONResponse(response);
  }
}
